// src/neetjee_guru/components/academicPlanner/CalendarGridViewport.js
import React, { Component } from "react";

// Subcomponent for individual calendar cell, optimized to prevent re-renders
class CalendarCell extends Component {
  shouldComponentUpdate(nextProps) {
    const { day, activeSubjects, isSelected, searchText, isDimmed } = this.props;
    return (
      nextProps.day.date !== day.date ||
      nextProps.day.type !== day.type ||
      nextProps.day.title !== day.title ||
      nextProps.day.topics !== day.topics ||
      nextProps.isSelected !== isSelected ||
      nextProps.searchText !== searchText ||
      nextProps.isDimmed !== isDimmed ||
      JSON.stringify(nextProps.activeSubjects) !== JSON.stringify(activeSubjects)
    );
  }

  render() {
    const { day, activeSubjects, isSelected, onDayClick, streamName, isDimmed } = this.props;

    // Determine topics to render dynamically matching live database keys
    let subjectsToRender = [];
    if (day.topics && Object.keys(day.topics).length > 0) {
      const dbSubjects = Object.keys(day.topics);
      if (activeSubjects && activeSubjects.length > 0) {
        subjectsToRender = dbSubjects.filter(s => 
          activeSubjects.includes(s) || 
          activeSubjects.some(a => s.toLowerCase().includes(a.toLowerCase()))
        );
        if (subjectsToRender.length === 0) {
          subjectsToRender = dbSubjects;
        }
      } else {
        subjectsToRender = dbSubjects;
      }
    } else {
      subjectsToRender = activeSubjects && activeSubjects.length > 0 ? activeSubjects : [];
    }

    // Extract day number and month
    const dayNumber = new Date(day.date).getDate();
    const monthName = new Date(day.date).toLocaleDateString("en-US", { month: "short" });

    // Styles for cell based on classification
    let cellClass = "calendar-grid-cell ds-surface p-2 d-flex flex-column justify-content-between ";
    
    if (day.type === "Exam") {
      cellClass += "is-exam-cell";
    } else if (day.type === "Holiday") {
      cellClass += "is-holiday-cell";
    } else {
      cellClass += "is-regular-cell";
    }

    if (isSelected) {
      cellClass += " is-selected-cell";
    }

    if (isDimmed) {
      cellClass += " is-dimmed";
    }

    const todayDateObj = new Date();
    const todayYear = todayDateObj.getFullYear();
    const todayMonth = String(todayDateObj.getMonth() + 1).padStart(2, '0');
    const todayDay = String(todayDateObj.getDate()).padStart(2, '0');
    const todayStr = `${todayYear}-${todayMonth}-${todayDay}`;
    const isToday = day.date === todayStr || day.isToday === true;

    return (
      <div
        id={`calendar-cell-${day.date}`}
        className={`${cellClass} ${isToday ? "today-target-cell" : ""}`}
        onClick={() => onDayClick(day)}
      >
        {/* Cell Header: Date Number and type label indicators */}
        <div className="cell-header d-flex justify-content-between align-items-start border-bottom pb-1">
          <span className={`cell-date-num font-weight-bold ${isToday ? "today-num-badge" : "text-dark"}`}>
            {dayNumber} <span className="small text-muted font-weight-normal">{monthName}</span>
          </span>

          <div className="d-flex gap-1 align-items-center">
            {day.type === "Exam" && (
              <span className="badge badge-info text-white text-xs font-weight-bold px-1.5 py-0.5" style={{ fontSize: "8.5px", borderRadius: "3px" }} title="Exam Day">
                EXAM
              </span>
            )}
            {day.type === "Holiday" && (
              <span className="badge badge-danger text-white text-xs font-weight-bold px-1.5 py-0.5" style={{ fontSize: "8.5px", borderRadius: "3px" }} title="Holiday">
                HOLIDAY
              </span>
            )}
          </div>
        </div>

        {/* Cell Body: Subject Topics mapped to micro-pills */}
        <div className="cell-body mt-2 flex-grow-1 overflow-hidden" style={{ maxHeight: "95px" }}>
          {day.type !== "Regular" ? (
            <div className="special-event-label font-weight-bold mt-1 text-xs" style={{ color: day.type === "Exam" ? "var(--ds-info)" : "var(--ds-crit)" }}>
              {day.title}
              <div className="text-muted font-weight-normal text-truncate mt-1" style={{ fontSize: "10px" }}>
                {day.description}
              </div>
            </div>
          ) : (
            <div className="cell-subject-topics d-flex flex-column gap-1">
              {subjectsToRender.map(subject => {
                const topic = day.topics[subject] || "";
                if (!topic) return null;
                
                const s = subject.trim().toLowerCase();
                let label = subject.substring(0, 4).toUpperCase();
                let pillBg = "#f1f5f9";
                let pillColor = "#334155";
                let pillBorder = "#64748b";

                if (s.includes("math")) {
                  label = s.includes("02") || s.includes("track 2") ? "M2" : (s.includes("01") || s.includes("track 1") ? "M1" : "MATH");
                  pillBg = "#faf5ff"; pillColor = "#6b21a8"; pillBorder = "#8b5cf6";
                } else if (s.includes("physic") || s.includes("phy")) {
                  label = "PHY";
                  pillBg = "#eff6ff"; pillColor = "#1e40af"; pillBorder = "#3b82f6";
                } else if (s.includes("chemist") || s.includes("chem")) {
                  label = "CHEM";
                  pillBg = "#f0fdf4"; pillColor = "#065f46"; pillBorder = "#10b981";
                } else if (s.includes("botan") || s.includes("bot")) {
                  label = "BOT";
                  pillBg = "#ecfdf5"; pillColor = "#047857"; pillBorder = "#059669";
                } else if (s.includes("zoolog") || s.includes("zoo")) {
                  label = "ZOO";
                  pillBg = "#fffbeb"; pillColor = "#92400e"; pillBorder = "#f59e0b";
                }

                // Strip prefixes
                const displayTopic = topic
                  .replace("Advanced Focus: ", "")
                  .replace("AP Specific: ", "")
                  .replace("TS Specific: ", "")
                  .replace("Board (IPE): ", "");

                return (
                  <div
                    key={subject}
                    className="cell-topic-row p-1 rounded d-flex align-items-center gap-1"
                    style={{
                      background: pillBg,
                      borderLeft: `3px solid ${pillBorder}`,
                      fontSize: "10.5px",
                      lineHeight: "1.2"
                    }}
                  >
                    <span className="font-weight-bold px-1 rounded text-white" style={{ background: pillBorder, fontSize: "9px" }}>
                      {label}
                    </span>
                    <span className="text-truncate font-weight-bold" style={{ color: pillColor }}>
                      {displayTopic}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    );
  }
}

class CalendarGridViewport extends Component {
  render() {
    const { filteredDays, allDays, activeSubjects, onDayClick, selectedDay, searchText, streamName } = this.props;

    // Weekdays headers
    const weekdays = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
    
    // Resolve all days array for rendering context
    const daysToRender = allDays || filteredDays;

    return (
      <div className="calendar-viewport-wrapper p-3 ds-surface-card rounded-lg shadow-sm border border-light">
        
        {/* Calendar Title & Legend Row */}
        <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center border-bottom pb-2 mb-3 gap-2">
          <div>
            <h5 className="font-weight-black text-dark m-0">
              Calendar Matrix Grid
            </h5>
            <p className="text-muted m-0 small mt-0.5">
              Full Academic Year Matrix ({daysToRender ? daysToRender.length : 0} Total Schedule Days)
            </p>
          </div>
          
          {/* Visual Legend Bar */}
          <div className="calendar-legend-bar d-flex gap-3 align-items-center bg-light px-3 py-1.5 rounded-lg border text-xs">
            <span className="d-flex align-items-center gap-1.5 font-weight-semibold text-muted">
              <span className="legend-dot" style={{ backgroundColor: "#7c3aed" }} /> Lessons
            </span>
            <span className="d-flex align-items-center gap-1.5 font-weight-semibold text-muted">
              <span className="legend-dot" style={{ backgroundColor: "#0284c7" }} /> Exams
            </span>
            <span className="d-flex align-items-center gap-1.5 font-weight-semibold text-muted">
              <span className="legend-dot" style={{ backgroundColor: "#e11d48" }} /> Holidays
            </span>
          </div>
        </div>
        
        {/* Calendar Weekday Names Header Row */}
        <div className="calendar-grid-header mb-2">
          {weekdays.map(day => (
            <div key={day} className="calendar-header-col text-center py-2 font-weight-black text-muted text-uppercase small" style={{ letterSpacing: "0.06em", fontSize: "11px" }}>
              {day.slice(0, 3)}
            </div>
          ))}
        </div>

        {/* Calendar Days Matrix */}
        {daysToRender.length === 0 ? (
          <div className="text-center p-5 text-muted">
            <i className="fal fa-search-minus display-4 mb-2" />
            <p>No days match the active search and classification filters.</p>
          </div>
        ) : (
          <div className="calendar-grid-body">
            {daysToRender.map(day => {
              const isSelected = selectedDay && selectedDay.date === day.date;
              
              // Determine if this day matches current filters
              const isMatched = filteredDays.some(fd => fd.date === day.date);

              return (
                <CalendarCell
                  key={day.date}
                  day={day}
                  activeSubjects={activeSubjects}
                  isSelected={isSelected}
                  onDayClick={onDayClick}
                  searchText={searchText}
                  streamName={streamName}
                  isDimmed={!isMatched}
                />
              );
            })}
          </div>
        )}
      </div>
    );
  }
}

export default CalendarGridViewport;
